import { useMemo, useState } from 'react';

import { Button, Checkbox, Flex, Header, Text } from '@electricjs/arc';

import { AppsByGroup } from '@/components/People/EmployeeAppsByGroup/useGetApplicationsForGroups';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getDisplayFullName } from 'common/utils/getFullName';
import { CenteredSpinner } from '@common';
import { useGetEmployeeFromDirectoryQuery } from '@/redux/slices/employeeApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { SelectionParamKeys } from '@/types/queryParamKeys';
import { useGetEmployeeOrgApplicationAccounts } from '@/hooks/useGetEmployeeOrgApplicationAccounts';
import { useGetOrganizationApplicationsQuery } from '@/redux/slices/organizationApiSlice';
import { useTranslation } from 'react-i18next';
import { Group } from '@/types/groups';
import { ManagedAppsTable } from '@/components/Applications/ProvisioningAppsTable/ManagedAppsTable';
import { ExternalAppsTable } from '@/components/Applications/ProvisioningAppsTable/ExternalAppsTable';
import {
  ManagedAppRow,
  ExternalAppRow,
} from '@/components/Applications/ProvisioningAppsTable/types';

type ApplicationsProps = {
  appsByGroup: AppsByGroup[] | undefined;
  onNext: () => void;
  isSubmitting: boolean;
};

const Applications = ({
  appsByGroup,
  isSubmitting,
  onNext,
}: ApplicationsProps) => {
  const { t } = useTranslation('common');
  const [userUnderstands, setUserUnderstands] = useState(false);

  const organizationId = useGetOrganizationId();
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const employeeId = queryParams.get(SelectionParamKeys.EmployeeId) || '';

  const { employeeName, isFetching: isFetchingEmployee } =
    useGetEmployeeFromDirectoryQuery(
      {
        employeeId,
        organizationId,
      },
      {
        skip: !employeeId,
        selectFromResult: ({ data, isFetching }) => ({
          employeeName: getDisplayFullName(data),
          isFetching,
        }),
      }
    );

  const {
    employeeOrgApplicationAccounts,
    isLoading: isLoadingEmployeeOrgApplicationAccounts,
  } = useGetEmployeeOrgApplicationAccounts({
    employeeId,
    skip: false,
  });

  const {
    data: orgApplicationsData,
    isLoading: isLoadingOrgApps,
    isError: isErrorOrgApps,
  } = useGetOrganizationApplicationsQuery({ organizationId, limit: 1000 });

  const orgAppMap = useMemo(() => {
    return new Map(
      (orgApplicationsData?.applications ?? []).map(a => [a.application.id, a])
    );
  }, [orgApplicationsData]);

  const appGroupsLookup = useMemo(() => {
    const map = new Map<string, Group[]>();
    (appsByGroup ?? []).forEach(({ group, applications }) => {
      applications.forEach(app => {
        const existing = map.get(app.id);
        if (existing) {
          existing.push(group);
        } else {
          map.set(app.id, [group]);
        }
      });
    });
    return map;
  }, [appsByGroup]);

  const { managedRows, externalRows } = useMemo(() => {
    const managed: ManagedAppRow[] = [];
    const external: ExternalAppRow[] = [];

    (employeeOrgApplicationAccounts ?? []).forEach(account => {
      const { application } = account;
      const orgApp = orgAppMap.get(application.id);
      const groups = appGroupsLookup.get(application.id) ?? [];
      const isDisabled = orgApp?.disableDeleteAccountRequests ?? false;

      if (isDisabled) {
        external.push({
          appId: application.id,
          appName: application.name,
          appIcon: application.icon,
          groups,
        });
      } else {
        const isAutomated = Boolean(
          orgApp?.integrationConnected && orgApp?.offboardingAutomationOn
        );
        managed.push({
          appId: application.id,
          appName: application.name,
          appIcon: application.icon,
          appSlug: application.slug,
          isAutomated,
          groups,
        });
      }
    });

    return { managedRows: managed, externalRows: external };
  }, [employeeOrgApplicationAccounts, orgAppMap, appGroupsLookup]);

  const handleCancel = () => {
    navigate(`/employees/${employeeId}`);
  };

  if (
    isFetchingEmployee ||
    isLoadingEmployeeOrgApplicationAccounts ||
    isLoadingOrgApps
  ) {
    return <CenteredSpinner />;
  }

  const title = t('offboardings.applications.title');
  const subtitle = t('offboardings.applications.subtitle', { employeeName });

  return (
    <>
      <Header title={title} subtitle={subtitle} />
      <Flex flexDirection="column" width="100%" mt="4rem">
        {isErrorOrgApps && (
          <Text variant="body-2" m="2rem">
            {t('offboardings.applications.errorLoadingApps')}
          </Text>
        )}

        {!isErrorOrgApps && managedRows.length > 0 && (
          <ManagedAppsTable
            rows={managedRows}
            tableId="offboarding-managed-apps-table"
            sectionHeader={t('offboardings.applications.managedSectionHeader', {
              count: managedRows.length,
            })}
            automationColumnHeader={t(
              'offboardings.applications.columnRevokeMethod'
            )}
            applicationColumnHeader={t(
              'offboardings.applications.columnApplication'
            )}
            groupsColumnHeader={t('offboardings.applications.columnGroups')}
          />
        )}

        {!isErrorOrgApps && externalRows.length > 0 && (
          <ExternalAppsTable
            rows={externalRows}
            tableId="offboarding-external-apps-table"
            sectionHeader={t(
              'offboardings.applications.externalSectionHeader',
              { count: externalRows.length }
            )}
            applicationColumnHeader={t(
              'offboardings.applications.columnApplication'
            )}
          />
        )}
      </Flex>
      <Flex vAlignContent="center" m="2rem 0 0 2rem">
        <Checkbox
          checked={userUnderstands}
          onClick={() => {
            setUserUnderstands(oldValue => !oldValue);
          }}
          id="user-understands-checkbox"
          disabled={isErrorOrgApps}
        />
        <Text>
          <label htmlFor="user-understands-checkbox">
            {t('offboardings.applications.checkboxLabel')}
          </label>
        </Text>
      </Flex>
      <Flex mt="9rem" hAlignContent="right" gap="3.4rem" alignSelf="flex-end">
        <Button
          id="applications-revoke-cancel-button"
          variant="text"
          disabled={isSubmitting}
          onClick={handleCancel}>
          {t('offboardings.applications.cancelButton')}
        </Button>
        <Button
          id="applications-revoke-button"
          onClick={onNext}
          disabled={!userUnderstands || isErrorOrgApps}
          loading={isSubmitting}>
          {t('offboardings.applications.revokeAccessButton')}
        </Button>
      </Flex>
    </>
  );
};

export default Applications;
